import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import { useInRouterContext, useSearchParams } from 'react-router-dom';
import { useUserStore } from '../../../stores/userStore';
import useAdminDraftGuard from '../useAdminDraftGuard';
import ReactQuillEditor from '../../../components/Editor/ReactQuillEditor';
import FeedBody from '../../feed/FeedBody';
import FeedPushMusicPicker from './FeedPushMusicPicker';
import RepresentativeImageSelector, {
  extractImageUrls,
  getRepresentativeImageUrl,
} from '../../../components/common/RepresentativeImageSelector';

const emptyForm = {
  title: '',
  titleEn: '',
  content: '',
  contentEn: '',
  imageUrl: '',
  memberGateAfterBlock: '',
  memberGateAfterBlockEn: '',
  isHidden: false,
  isSponsored: false,
  pushSelected: false,
  pushAlbumIds: [],
  pushArtistIds: [],
  pushGenres: [],
  pushAlbumLabels: {},
  pushArtistLabels: {},
};

export const contentBlocks = (html = '') => {
  if (typeof DOMParser === 'undefined') return [];
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  return Array.from(body.children).map((element, index) => ({
    index: index + 1,
    label:
      element.textContent?.replace(/\s+/g, ' ').trim().slice(0, 45) ||
      (element.tagName === 'IMG' ? '이미지' : element.tagName.toLowerCase()),
  }));
};

function GateSelect({ html, label, value, onChange, disabled }) {
  const blocks = useMemo(() => contentBlocks(html), [html]);
  const labelId = useId();
  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel shrink htmlFor={labelId}>
        {label}
      </InputLabel>
      <Select
        native
        notched
        inputProps={{ id: labelId }}
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">전체 공개</option>
        <option value={0}>제목 직후부터 회원만 공개</option>
        {blocks.map((block) => (
          <option key={block.index} value={block.index}>
            {block.index}번째 블록 뒤 · {block.label}
          </option>
        ))}
      </Select>
    </FormControl>
  );
}

function FeedSession({ query, setQuery }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [notice, setNotice] = useState('');
  const [uploading, setUploading] = useState(false);
  const pending = useRef(false);
  const uploadPending = useRef(false);
  const lifecycle = useRef({ live: true, read: 0 });
  const locked = busy || uploading;
  useEffect(() => {
    const session = lifecycle.current;
    session.live = true;
    return () => {
      session.live = false;
      session.read += 1;
    };
  }, []);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editorLocale, setEditorLocale] = useState('ko');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(393);
  const [form, setForm] = useState(emptyForm);
  const draft = useAdminDraftGuard(formOpen ? form : emptyForm, {
    enabled: formOpen || locked,
    busy: locked,
  });
  const requestClose = () => {
    if (pending.current || uploadPending.current || !draft.confirmDiscard()) return;
    setPreviewOpen(false);
    setFormOpen(false);
  };
  const hasContentImages = useMemo(() => extractImageUrls(form.content).length > 0, [form.content]);

  const load = useCallback(async () => {
    const session = lifecycle.current;
    const request = ++session.read;
    const current = () => session.live && session.read === request;
    setLoading(true);
    setListError('');
    try {
      const params = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
      const response = await api.getAuth(`/admin/app/feeds${params}`);
      if (current()) setItems(response.data.data ?? []);
    } catch (failure) {
      if (current()) setListError(failure.message || '피드 목록을 불러오지 못했습니다.');
    } finally {
      if (current()) setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const session = lifecycle.current;
    session.read += 1;
    setItems([]);
    setLoading(true);
    setListError('');
    setNotice('');
    const timeout = setTimeout(() => {
      void load();
    }, 250);
    return () => {
      clearTimeout(timeout);
      session.read += 1;
    };
  }, [load]);

  const openCreate = () => {
    if (pending.current) return;
    setPreviewOpen(false);
    setPreviewWidth(393);
    draft.resetBaseline();
    setEditingId(null);
    setForm(emptyForm);
    setEditorLocale('ko');
    setError('');
    setFormOpen(true);
  };
  const openEdit = (item) => {
    if (pending.current || loading) return;
    setPreviewOpen(false);
    setPreviewWidth(393);
    draft.resetBaseline();
    setEditingId(item.id);
    const initial = {
      title: item.title ?? '',
      titleEn: item.titleEn ?? '',
      content: item.content ?? '',
      contentEn: item.contentEn ?? '',
      imageUrl: item.imageUrl ?? '',
      memberGateAfterBlock: item.memberGateAfterBlock ?? '',
      memberGateAfterBlockEn: item.memberGateAfterBlockEn ?? '',
      isHidden: Boolean(item.isHidden),
      isSponsored: Boolean(item.isSponsored),
      pushSelected: Boolean(item.pushSelected),
      pushAlbumIds: item.pushAlbumIds || [],
      pushArtistIds: item.pushArtistIds || [],
      pushGenres: item.pushGenres || [],
      pushAlbumLabels: item.pushAlbumLabels || {},
      pushArtistLabels: item.pushArtistLabels || {},
    };
    setForm(initial);
    setEditorLocale('ko');
    setError('');
    setFormOpen(true);
  };
  const change = (key, value) => {
    if (!pending.current && lifecycle.current.live)
      setForm((current) => ({ ...current, [key]: value }));
  };
  const uploadChanged = (value) => {
    uploadPending.current = value;
    if (lifecycle.current.live) setUploading(value);
  };
  const save = async () => {
    if (pending.current || uploadPending.current) return;
    // An explicitly selected or entered cover remains authoritative. Only fall
    // back to the first body image when the cover field is empty.
    const imageUrl = form.imageUrl.trim() || getRepresentativeImageUrl(form.content, '', '');
    if (!form.title.trim() || !form.content.trim() || !imageUrl.trim()) {
      setError('한국어 제목·본문과 대표 이미지를 모두 입력해주세요.');
      return;
    }
    const { pushAlbumLabels, pushArtistLabels, ...editableForm } = form;
    const payload = {
      ...editableForm,
      pushGenres: form.pushGenres.map((value) => value.trim()).filter(Boolean),
      title: form.title.trim(),
      titleEn: form.titleEn.trim() || null,
      contentEn: form.contentEn.trim() || null,
      imageUrl: imageUrl.trim(),
      memberGateAfterBlock:
        form.memberGateAfterBlock === '' ? null : Number(form.memberGateAfterBlock),
      memberGateAfterBlockEn:
        form.contentEn.trim() && form.memberGateAfterBlockEn !== ''
          ? Number(form.memberGateAfterBlockEn)
          : null,
    };
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const response = editingId
        ? await api.putAuth(`/admin/app/feeds/${editingId}`, payload)
        : await api.postAuth('/admin/app/feeds', payload);
      if (!lifecycle.current.live) return;
      // Keep the confirmed write even if the following refresh fails.
      const saved = response?.data?.data;
      setItems((current) =>
        editingId
          ? current.map((item) => (item.id === editingId ? { ...item, ...payload } : item))
          : saved?.id
            ? [{ ...saved, ...payload }, ...current]
            : current,
      );
      draft.markClean();
      setFormOpen(false);
      setPreviewOpen(false);
      setNotice('피드를 저장했습니다.');
      await load();
    } catch (failure) {
      if (lifecycle.current.live) setError(failure.message || '피드를 저장하지 못했습니다.');
    } finally {
      pending.current = false;
      if (lifecycle.current.live) setBusy(false);
    }
  };
  const toggleVisibility = async (item) => {
    if (pending.current || loading || uploadPending.current) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await api.putAuth(
        `/admin/app/feeds/${item.id}/visibility`,
        { isHidden: !item.isHidden, expectedIsHidden: Boolean(item.isHidden) },
        { suppressGlobalError: true },
      );
      if (!lifecycle.current.live) return;
      const saved = response.data.data;
      setItems((current) => current.map((row) => (row.id === item.id ? saved : row)));
      setNotice(
        saved.isHidden
          ? '피드를 숨겼습니다. 관리자 목록에서는 계속 확인할 수 있습니다.'
          : '피드를 공개했습니다.',
      );
      await load();
    } catch (failure) {
      if (!lifecycle.current.live) return;
      setError(failure.message || '피드 공개 상태를 변경하지 못했습니다.');
      await load();
    } finally {
      pending.current = false;
      if (lifecycle.current.live) setBusy(false);
    }
  };
  const remove = async (item) => {
    if (pending.current || loading) return;
    if (!window.confirm(`“${item.title}” 피드를 삭제할까요? 이 작업은 되돌릴 수 없습니다.`)) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.deleteAuth(`/admin/app/feeds/${item.id}`);
      if (!lifecycle.current.live) return;
      setItems((current) => current.filter((row) => row.id !== item.id));
      setNotice('피드를 삭제했습니다.');
      await load();
    } catch (failure) {
      if (lifecycle.current.live) setError(failure.message || '피드를 삭제하지 못했습니다.');
    } finally {
      pending.current = false;
      if (lifecycle.current.live) setBusy(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography variant="h6">포스트팝 피드</Typography>
          <Typography variant="body2" color="text.secondary">
            피드의 본문·대표 이미지와 공개 범위를 관리합니다. 숨긴 피드도 관리자 목록에 남습니다.
          </Typography>
        </Box>
        <Button disabled={busy} variant="contained" onClick={openCreate}>
          새 피드 작성
        </Button>
      </Stack>
      <TextField
        disabled={busy}
        label="제목 검색"
        size="small"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {error && !formOpen ? <Alert severity="error">{error}</Alert> : null}
      {notice && !formOpen ? <Alert severity="success">{notice}</Alert> : null}
      {listError && !formOpen ? (
        <Alert
          severity="error"
          sx={{
            flexWrap: 'wrap',
            '& .MuiAlert-message': { minWidth: 0, flex: '1 1 120px', overflowWrap: 'anywhere' },
            '& .MuiAlert-action': { ml: 'auto', pl: 0 },
          }}
          action={
            <Button disabled={busy || loading} onClick={load}>
              다시 불러오기
            </Button>
          }
        >
          {notice ? '변경은 완료됐지만 목록을 새로 불러오지 못했습니다. ' : ''}
          {listError}
        </Alert>
      ) : null}
      {loading ? <Typography>불러오는 중…</Typography> : null}
      {!loading && !listError && !items.length ? (
        <Alert severity="info">등록된 피드가 없습니다.</Alert>
      ) : null}
      <Stack spacing={1.25}>
        {items.map((item) => (
          <Paper
            key={item.id}
            variant="outlined"
            sx={{ p: 1.5, minWidth: 0, containerType: 'inline-size' }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                '@container (max-width: 450px)': {
                  flexWrap: 'wrap',
                  gap: 1,
                  '& > :not(style) ~ :not(style)': { ml: 0 },
                },
              }}
            >
              <Box
                component="img"
                src={item.imageUrl}
                alt=""
                sx={{ width: 72, height: 72, flexShrink: 0, objectFit: 'cover', borderRadius: 1 }}
              />
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  '@container (max-width: 450px)': { flexBasis: 'calc(100% - 90px)' },
                }}
              >
                <Typography fontWeight={700} noWrap>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.createdAt} · {item.isHidden ? '숨김' : '공개'} ·{' '}
                  {item.memberGateAfterBlock == null
                    ? '전체 공개'
                    : `${item.memberGateAfterBlock}개 블록 공개 후 회원 잠금`}
                </Typography>
              </Box>
              <Button
                component="a"
                href={`/feed/${item.id}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${item.title} 피드 미리보기`}
              >
                미리보기
              </Button>
              <Button
                disabled={busy || loading}
                variant="outlined"
                aria-label={`${item.title} 피드 ${item.isHidden ? '공개' : '숨김'}`}
                onClick={() => toggleVisibility(item)}
              >
                {item.isHidden ? '공개' : '숨김'}
              </Button>
              <Button disabled={busy || loading} onClick={() => openEdit(item)}>
                수정
              </Button>
              <Button disabled={busy || loading} color="error" onClick={() => remove(item)}>
                삭제
              </Button>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog
        fullWidth
        maxWidth="md"
        open={formOpen}
        onClose={requestClose}
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 4 },
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          },
          '& .MuiDialogContent-root': { px: { xs: 1.5, sm: 3 } },
        }}
      >
        <DialogTitle>{editingId ? '피드 수정' : '새 피드 작성'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              disabled={locked}
              required
              label="제목 (한국어)"
              value={form.title}
              onChange={(event) => change('title', event.target.value)}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              disabled={locked}
              label="제목 (영어, 선택)"
              value={form.titleEn}
              onChange={(event) => change('titleEn', event.target.value)}
              inputProps={{ maxLength: 255 }}
            />
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={editorLocale}
              onChange={(_, value) => setEditorLocale(value)}
            >
              <Tab disabled={locked} value="ko" label="한국어 본문" />
              <Tab disabled={locked} value="en" label="영어 본문 (선택)" />
            </Tabs>
            <ReactQuillEditor
              readOnly={busy}
              onUploadPendingChange={uploadChanged}
              feedLinks
              toolbarTop={0}
              key={editorLocale}
              value={editorLocale === 'ko' ? form.content : form.contentEn}
              onChange={(value) => change(editorLocale === 'ko' ? 'content' : 'contentEn', value)}
            />
            <Button
              disabled={locked}
              variant="outlined"
              aria-expanded={previewOpen}
              onClick={() => setPreviewOpen((value) => !value)}
            >
              {previewOpen ? '본문 미리보기 닫기' : '본문 미리보기'}
            </Button>
            {previewOpen && (
              <Stack spacing={2}>
                <FormControl fullWidth disabled={locked}>
                  <InputLabel shrink htmlFor="feed-preview-width">
                    미리보기 화면
                  </InputLabel>
                  <Select
                    native
                    inputProps={{ id: 'feed-preview-width' }}
                    label="미리보기 화면"
                    value={previewWidth}
                    onChange={(event) => setPreviewWidth(Number(event.target.value))}
                  >
                    <option value={393}>모바일 · 393px</option>
                    <option value={760}>PC · 본문 760px</option>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  선택한 언어의 전체 본문입니다. 비회원에게 보이는 범위는 아래 공개 범위 설정을
                  따릅니다.
                </Typography>
                <Box
                  role="region"
                  aria-label="피드 본문 미리보기"
                  sx={{
                    width: '100%',
                    maxWidth: previewWidth,
                    mx: 'auto',
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography
                    component="h2"
                    sx={{ mb: 2, fontWeight: 700, overflowWrap: 'anywhere' }}
                  >
                    {editorLocale === 'ko' ? form.title : form.titleEn || form.title}
                  </Typography>
                  <FeedBody html={editorLocale === 'ko' ? form.content : form.contentEn} />
                </Box>
              </Stack>
            )}
            {editorLocale === 'ko' ? (
              <>
                <GateSelect
                  disabled={locked}
                  html={form.content}
                  label="비회원 공개 범위 (한국어)"
                  value={form.memberGateAfterBlock}
                  onChange={(value) => change('memberGateAfterBlock', value)}
                />
                {hasContentImages ? (
                  <RepresentativeImageSelector
                    autoSelect={false}
                    disabled={locked}
                    content={form.content}
                    value={form.imageUrl}
                    onChange={(value) => change('imageUrl', value)}
                  />
                ) : null}
              </>
            ) : (
              <GateSelect
                disabled={locked}
                html={form.contentEn}
                label="비회원 공개 범위 (영어)"
                value={form.memberGateAfterBlockEn}
                onChange={(value) => change('memberGateAfterBlockEn', value)}
              />
            )}
            <TextField
              disabled={locked}
              required
              label="대표 이미지 URL"
              helperText="본문 이미지 중 선택하거나 http(s) 이미지 주소를 입력하세요. GIF도 사용할 수 있습니다."
              value={form.imageUrl}
              onChange={(event) => change('imageUrl', event.target.value)}
            />
            {form.imageUrl ? (
              <Box
                component="img"
                src={form.imageUrl}
                alt="대표 이미지 미리보기"
                sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1 }}
              />
            ) : null}
            <FormControlLabel
              control={
                <Checkbox
                  disabled={locked}
                  checked={form.isHidden}
                  onChange={(event) => change('isHidden', event.target.checked)}
                />
              }
              label="일반 사용자에게 숨김"
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={locked}
                  checked={form.isSponsored}
                  onChange={(event) => change('isSponsored', event.target.checked)}
                />
              }
              label="광고 콘텐츠"
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={locked}
                  checked={form.pushSelected}
                  onChange={(event) => change('pushSelected', event.target.checked)}
                />
              }
              label="알림으로 소개할 글"
            />
            {form.pushSelected ? (
              <Stack spacing={2}>
                <Alert severity="info">
                  공개된 글을 저녁에 소개합니다. 회원당 피드 주 1회, 음악 발견·기록·이벤트 합산 주
                  3회 한도 안에서 선정하며, 이미 읽은 회원에게는 보내지 않습니다. 수정하거나 다시
                  공개해도 같은 글을 재발송하지 않습니다.
                  {form.isSponsored
                    ? ' 광고 글은 이벤트 알림과 마케팅 수신에 모두 동의한 회원에게만 소개합니다.'
                    : ''}
                </Alert>
                {form.isHidden ? (
                  <Alert severity="warning">숨김 상태에서는 발송하지 않습니다.</Alert>
                ) : null}
                <FeedPushMusicPicker
                  kind="albums"
                  label="관련 앨범"
                  ids={form.pushAlbumIds}
                  labels={form.pushAlbumLabels}
                  disabled={locked}
                  onChange={(value) => change('pushAlbumIds', value)}
                />
                <FeedPushMusicPicker
                  kind="artists"
                  label="관련 아티스트"
                  ids={form.pushArtistIds}
                  labels={form.pushArtistLabels}
                  disabled={locked}
                  onChange={(value) => change('pushArtistIds', value)}
                />
                <TextField
                  disabled={locked}
                  label="관련 장르"
                  value={form.pushGenres.join(', ')}
                  helperText="앱에 표시되는 장르 이름을 쉼표로 구분해주세요. 예: 재즈, 소울"
                  onChange={(event) =>
                    change(
                      'pushGenres',
                      event.target.value
                        .split(',')
                        .map((value) => value.trim())
                        .slice(0, 10),
                    )
                  }
                />
              </Stack>
            ) : null}
            <Alert severity="info">
              공개 전에 검수하려면 숨김으로 저장한 뒤 목록의 미리보기를 누르세요. 숨긴 피드는 관리자
              계정으로만 볼 수 있으며, 확인 후 목록에서 공개할 수 있습니다. 공개된 피드의 회원 잠금
              이후 본문은 로그인 회원에게만 표시됩니다.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={locked} onClick={requestClose}>
            취소
          </Button>
          <Button disabled={locked} variant="contained" onClick={save}>
            {busy ? '저장 중…' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function RoutedFeed({ account }) {
  const [params, setParams] = useSearchParams();
  return (
    <FeedSession
      key={account}
      query={params.get('feedQuery') || ''}
      setQuery={(value) => {
        const next = new URLSearchParams(params);
        if (value) next.set('feedQuery', value);
        else next.delete('feedQuery');
        setParams(next, { replace: true });
      }}
    />
  );
}
function LocalFeed() {
  const [query, setQuery] = useState('');
  return <FeedSession query={query} setQuery={setQuery} />;
}
export default function FeedManagement() {
  const user = useUserStore((state) => state.user);
  const account = JSON.stringify([user?.id, user?.role]);
  const routed = useInRouterContext();
  return routed ? <RoutedFeed account={account} /> : <LocalFeed key={account} />;
}
